import { Controller } from "@hotwired/stimulus"
import Sortable from 'sortablejs';

export default class extends Controller {
  connect() {
    this.initializeSortables();
    this.initializeDropdowns();
    this.setupEscapeHandler();
    this.isDragging = false;
    this.lastHoveredCategory = null;
  }

  disconnect() {
    if (this.sortables) {
      this.sortables.forEach(sortable => sortable.destroy());
    }
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
    }
    if (this.expandTimeout) {
      clearTimeout(this.expandTimeout);
    }
    this.cleanupDragState();
  }

  initializeSortables() {
    this.sortables = [];

    // First, temporarily show all category children to ensure sortables are initialized
    const allCategoryChildren = this.element.querySelectorAll('.category-children');
    const originalDisplayStates = [];
    allCategoryChildren.forEach(children => {
      originalDisplayStates.push(children.style.display);
      children.style.display = 'block';
    });

    // Find all sortable containers (including nested ones)
    const containers = this.element.querySelectorAll('[data-sortable-container="true"]');

    containers.forEach((container) => {
      const sortable = new Sortable(container, {
        group: {
          name: 'nested-templates',
          put: true,
          pull: true
        },
        animation: 200,
        easing: "cubic-bezier(0.4, 0.0, 0.2, 1)",
        fallbackOnBody: true,
        swapThreshold: 0.5,
        handle: '.template-card-drag-handle',
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        dragClass: 'sortable-drag',
        direction: 'vertical',
        removeCloneOnHide: true,
        revertOnSpill: true,
        scroll: true,
        scrollSensitivity: 60,
        scrollSpeed: 15,
        bubbleScroll: true,

        onStart: (evt) => {
          this.isDragging = true;
          document.body.classList.add('is-dragging');
          this.clearDragOverClasses();
        },

        onEnd: (evt) => {
          this.isDragging = false;
          try {
            this.handleDrop(evt);
          } finally {
            this.cleanupDragState();
          }
        },

        onMove: (evt) => {
          // Throttle onMove processing
          if (this.moveTimeout) return true;

          this.moveTimeout = setTimeout(() => {
            this.moveTimeout = null;
          }, 50);

          // Clear previous highlights efficiently
          if (this.lastHoveredCategory && this.lastHoveredCategory !== evt.related) {
            this.lastHoveredCategory.classList.remove('drag-over');
          }

          // Check if moving into a category's children area
          const toContainer = evt.to;
          const categoryId = toContainer.dataset.categoryId;
          const draggedItem = evt.dragged;

          // Find the category being dragged over
          let relatedCategory = null;
          if (categoryId) {
            relatedCategory = this.element.querySelector(`.category-card[data-id="${categoryId}"]`);
          }

          // Also check if hovering directly over a category card
          const related = evt.related;
          if (related) {
            if (related.classList.contains('category-card')) {
              relatedCategory = related;
            } else {
              const parentCategoryCard = related.closest('.category-card');
              if (parentCategoryCard && !relatedCategory) {
                relatedCategory = parentCategoryCard;
              }
            }
          }

          // Highlight the category being dragged over
          if (relatedCategory) {
            relatedCategory.classList.add('drag-over');
            this.lastHoveredCategory = relatedCategory;

            // Auto-expand category if closed (with delay)
            const categoryChildren = relatedCategory.querySelector('.category-children');
            if (categoryChildren && categoryChildren.style.display === 'none') {
              if (this.expandTimeout) clearTimeout(this.expandTimeout);

              this.expandTimeout = setTimeout(() => {
                const chevron = relatedCategory.querySelector('.category-chevron');
                categoryChildren.style.display = 'block';
                if (chevron) {
                  chevron.classList.remove('fa-chevron-right');
                  chevron.classList.add('fa-chevron-down');
                }
                relatedCategory.classList.add('category-open');

                // Lazily initialize sortable for newly expanded category
                const newContainer = categoryChildren.querySelector('[data-sortable-container="true"]');
                if (newContainer && !Sortable.get(newContainer)) {
                  this.initializeSingleContainer(newContainer);
                }
              }, 600);
            }
          }

          // Prevent dropping categories into their own children
          if (relatedCategory && draggedItem.dataset.type === 'category') {
            const draggedId = draggedItem.dataset.id;
            const targetCategoryId = relatedCategory.dataset.id;
            if (draggedId === targetCategoryId || this.isDescendant(draggedId, targetCategoryId)) {
              return false;
            }
          }

          if (draggedItem.dataset.type === 'category' && categoryId) {
            const draggedId = draggedItem.dataset.id;
            if (draggedId === categoryId || this.isDescendant(draggedId, categoryId)) {
              return false;
            }
          }

          return true;
        }
      });

      this.sortables.push(sortable);
    });

    // Restore original display states for category children
    allCategoryChildren.forEach((children, index) => {
      children.style.display = originalDisplayStates[index];
    });
  }

  // Initialize a single container (for lazy loading)
  initializeSingleContainer(container) {
    if (Sortable.get(container)) return;

    const sortable = new Sortable(container, {
      group: {
        name: 'nested-templates',
        put: true,
        pull: true
      },
      animation: 200,
      easing: "cubic-bezier(0.4, 0.0, 0.2, 1)",
      fallbackOnBody: true,
      swapThreshold: 0.5,
      handle: '.template-card-drag-handle',
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      dragClass: 'sortable-drag',
      direction: 'vertical',
      removeCloneOnHide: true,
      revertOnSpill: true,
      scroll: true,
      scrollSensitivity: 60,
      scrollSpeed: 15,
      bubbleScroll: true,
      onEnd: (evt) => {
        this.isDragging = false;
        try {
          this.handleDrop(evt);
        } finally {
          this.cleanupDragState();
        }
      }
    });

    this.sortables.push(sortable);
  }

  handleDrop(event) {
    const itemElement = event.item;
    const itemId = itemElement.dataset.id;
    const itemType = itemElement.dataset.type;

    // Check if we dropped directly onto a category card that was showing drag-over
    const dragOverCategory = this.element.querySelector('.category-card.drag-over');

    // Validate the drop target
    const sortableContainer = event.to ? event.to.dataset.sortableContainer : null;

    if (!event.to || sortableContainer !== "true") {
      this.reinitializeSortables();
      return;
    }

    // Check if the item actually moved
    const oldCategoryId = event.from.dataset.categoryId || null;
    const oldIndex = event.oldIndex;
    const newIndex = event.newIndex;

    // Determine new category
    let newCategoryId = event.to.dataset.categoryId;
    let newPosition = event.newIndex + 1;

    // If dropped onto a category card directly
    if (dragOverCategory) {
      const targetCategoryId = dragOverCategory.dataset.id;
      if (oldCategoryId !== targetCategoryId) {
        const categoryChildrenContainer = dragOverCategory.querySelector('.template-manager-level[data-sortable-container="true"]');
        if (categoryChildrenContainer) {
          const childrenCount = categoryChildrenContainer.querySelectorAll('.template-card, .category-card').length;
          newCategoryId = targetCategoryId;
          newPosition = childrenCount + 1;
        }
      }
    }

    // Normalize category IDs
    const normalizedOldCategoryId = (!oldCategoryId || oldCategoryId === 'null' || oldCategoryId === '') ? null : oldCategoryId;
    const normalizedNewCategoryId = (!newCategoryId || newCategoryId === 'null' || newCategoryId === '') ? null : newCategoryId;

    // Check if item was dropped in the same position
    if (normalizedOldCategoryId === normalizedNewCategoryId && oldIndex === newIndex) {
      return;
    }

    // Send update to server
    const categoryToExpand = normalizedNewCategoryId || normalizedOldCategoryId;
    this.updateItemPosition(itemId, itemType, normalizedNewCategoryId, newPosition, categoryToExpand);
  }

  clearDragOverClasses() {
    const categories = this.element.querySelectorAll('.category-card.drag-over');
    categories.forEach(category => category.classList.remove('drag-over'));
  }

  // Check if targetId is a descendant of categoryId
  isDescendant(categoryId, targetId) {
    const targetCategory = this.element.querySelector(`.category-card[data-id="${targetId}"]`);
    if (!targetCategory) return false;

    const categoryElement = this.element.querySelector(`.category-card[data-id="${categoryId}"]`);
    if (!categoryElement) return false;

    return targetCategory.contains(categoryElement);
  }

  updateItemPosition(itemId, itemType, categoryId, position, categoryToExpand) {
    const csrfToken = document.querySelector('[name="csrf-token"]').content;

    document.body.classList.add('updating-position');

    // Determine endpoint and payload based on item type
    const endpoint = itemType === 'category'
      ? `/template_categories/${itemId}/move`
      : `/page_templates/${itemId}/move`;

    const payload = itemType === 'category'
      ? {
        position: position,
        parent_id: categoryId
      }
      : {
        position: position,
        template_category_id: categoryId
      };

    fetch(endpoint, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to update position: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        const openCategories = this.getOpenCategoryIds();

        if (categoryToExpand && !openCategories.includes(categoryToExpand)) {
          openCategories.push(categoryToExpand);
        }

        if (openCategories.length > 0) {
          location.href = location.pathname + '?expand_categories=' + openCategories.join(',');
        } else {
          location.reload();
        }
      })
      .catch(error => {
        alert('Failed to move item. Please refresh and try again.');
        location.reload();
      })
      .finally(() => {
        document.body.classList.remove('updating-position');
      });
  }

  // Get IDs of all currently open/expanded categories
  getOpenCategoryIds() {
    const openCategories = [];
    const categoryCards = this.element.querySelectorAll('.category-card.category-open');

    categoryCards.forEach(card => {
      const categoryId = card.dataset.id;
      if (categoryId) {
        openCategories.push(categoryId);
      }
    });

    return openCategories;
  }

  // Reinitialize sortables when categories are toggled
  reinitializeSortables() {
    if (this.sortables) {
      this.sortables.forEach(sortable => sortable.destroy());
    }
    this.initializeSortables();
    this.initializeDropdowns();
  }

  // Initialize Bootstrap dropdowns
  initializeDropdowns() {
    if (typeof window.initializeAllDropdowns === 'function') {
      window.initializeAllDropdowns();
    } else if (typeof $ !== 'undefined' && $.fn.dropdown) {
      this.element.querySelectorAll('[data-toggle="dropdown"]').forEach(toggle => {
        const $toggle = $(toggle);
        if ($toggle.data('bs.dropdown')) {
          $toggle.dropdown('dispose');
        }
        $toggle.dropdown();
      });
    }
  }

  // Setup escape key handler to cancel stuck drags
  setupEscapeHandler() {
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.isDragging) {
        this.cleanupDragState();
        this.reinitializeSortables();
      }
    };
    document.addEventListener('keydown', this.escapeHandler);
  }

  // Centralized cleanup method for drag state
  cleanupDragState() {
    document.body.classList.remove('is-dragging');
    this.clearDragOverClasses();
    this.lastHoveredCategory = null;
    if (this.expandTimeout) {
      clearTimeout(this.expandTimeout);
      this.expandTimeout = null;
    }
    if (this.moveTimeout) {
      clearTimeout(this.moveTimeout);
      this.moveTimeout = null;
    }
  }
}

// Make functions globally available for inline event handlers
window.templateManagerController = {
  reinitialize: function () {
    const controller = document.querySelector('[data-controller="template-manager"]');
    if (controller && controller.templateManagerController) {
      controller.templateManagerController.reinitializeSortables();
    }
  }
};

